/**
 * Links from an integration out to the live storefront and to the platform's
 * own admin, built only from fields the integration doc already carries.
 * Never reads a token or secret, and never guesses a domain: a link whose
 * input is missing or malformed is left out.
 */

export interface IntegrationLinkSource {
  platform?: string;
  credentials?: Record<string, unknown> | null;
}

export interface IntegrationExternalLinks {
  storefront?: string;
  admin?: string;
  adminLabel: string;
}

const PLATFORM_NAMES: Record<string, string> = {
  shopify: 'Shopify',
  bigcommerce: 'BigCommerce',
  woocommerce: 'WooCommerce',
  lightspeed: 'Lightspeed',
  lightspeed_ecom: 'Lightspeed eCom',
};

const LIGHTSPEED_ADMIN_URL = 'https://cloud.lightspeedapp.com/';

const text = (value: unknown): string => (typeof value === 'string' ? value.trim() : '');

const HOSTNAME = /^[a-z0-9]([a-z0-9-]*[a-z0-9])?(\.[a-z0-9]([a-z0-9-]*[a-z0-9])?)+$/;
const SUBDOMAIN = /^[a-z0-9]([a-z0-9-]*[a-z0-9])?$/;

/** Same rule as `normalizeShopDomainForAdmin` in src/utils/shopifyThemeBlockLinks.ts. */
function normalizeShopDomain(shop: string): string {
  const trimmed = shop.trim().toLowerCase();
  if (!trimmed) return '';
  const bare = trimmed.replace(/^https?:\/\//, '').replace(/\/.*$/, '');
  if (!bare) return '';
  return bare.endsWith('.myshopify.com') ? bare : `${bare}.myshopify.com`;
}

function hostnameOf(value: string): string {
  const host = value.trim().toLowerCase().replace(/^https?:\/\//, '').replace(/[/?#].*$/, '');
  return HOSTNAME.test(host) ? host : '';
}

/** An http(s) URL reduced to origin + path, without query, hash or trailing slash. */
function webUrlOf(value: unknown): string {
  const raw = text(value);
  if (!raw) return '';
  try {
    const url = new URL(/^https?:\/\//i.test(raw) ? raw : `https://${raw}`);
    if (url.protocol !== 'https:' && url.protocol !== 'http:') return '';
    if (!HOSTNAME.test(url.hostname)) return '';
    return `${url.origin}${url.pathname}`.replace(/\/+$/, '');
  } catch {
    return '';
  }
}

function shopifyLinks(credentials: Record<string, unknown>): IntegrationExternalLinks {
  const myshopify = normalizeShopDomain(text(credentials.shopId) || text(credentials.shopDomain));
  const handle = myshopify.replace(/\.myshopify\.com$/, '');
  const validHandle = SUBDOMAIN.test(handle);
  const primary = hostnameOf(text(credentials.primaryDomain));
  const links: IntegrationExternalLinks = { adminLabel: 'Shopify admin' };
  if (primary) links.storefront = `https://${primary}`;
  else if (validHandle) links.storefront = `https://${handle}.myshopify.com`;
  if (validHandle) links.admin = `https://admin.shopify.com/store/${handle}`;
  return links;
}

function bigcommerceLinks(credentials: Record<string, unknown>): IntegrationExternalLinks {
  const hash = text(credentials.storeHash);
  const links: IntegrationExternalLinks = { adminLabel: 'BigCommerce admin' };
  const storefront = webUrlOf(credentials.storefrontUrl) || webUrlOf(credentials.storeUrl);
  if (storefront) links.storefront = storefront;
  if (/^[a-z0-9]+$/i.test(hash)) links.admin = `https://store-${hash.toLowerCase()}.mybigcommerce.com/manage`;
  return links;
}

function woocommerceLinks(credentials: Record<string, unknown>): IntegrationExternalLinks {
  const site = webUrlOf(credentials.storeUrl) || webUrlOf(credentials.siteUrl);
  const links: IntegrationExternalLinks = { adminLabel: 'WooCommerce admin' };
  if (site) {
    links.storefront = site;
    links.admin = `${site}/wp-admin`;
  }
  return links;
}

export function externalLinksFor(integration: IntegrationLinkSource | null | undefined): IntegrationExternalLinks {
  const platform = text(integration?.platform).toLowerCase();
  const credentials = integration?.credentials && typeof integration.credentials === 'object'
    ? integration.credentials
    : {};
  switch (platform) {
    case 'shopify':
      return shopifyLinks(credentials);
    case 'bigcommerce':
      return bigcommerceLinks(credentials);
    case 'woocommerce':
      return woocommerceLinks(credentials);
    case 'lightspeed':
      return { admin: LIGHTSPEED_ADMIN_URL, adminLabel: 'Lightspeed admin' };
    default:
      return { adminLabel: `${PLATFORM_NAMES[platform] || platform || 'Platform'} admin` };
  }
}
